import { dateToApiFormat } from '@/helpers/dateHelper';
import { useGetRostersOverviewQuery } from '@/services/gql/graphql.generated';
import { useLocationService } from '@/services/location/LocationService';
import { useMemo } from 'react';
import { RosterPeriodOption, useRosterPeriodDates } from './useRosterPeriodDates';
import { millisecondsToHours } from 'date-fns';

interface RosterPeriodDetails {
  displayString: string;
  titleString: string;
  displayCheck?: boolean;
  dates: Date[];
  warning?: boolean;
}

export const useRosterPeriodDetails = (): RosterPeriodDetails => {
  const option: RosterPeriodOption = 'month';
  const warningDays = option === 'month' ? 7 : 3;

  const dates = useRosterPeriodDates(option);

  if (dates.length === 0) {
    throw Error('Must provide a list of dates for roster period details.');
  }

  const selectedLocationId = useLocationService(s => s.selectedLocation?.id);

  if (!selectedLocationId) {
    throw Error('Location should be selected');
  }

  const [{ data }] = useGetRostersOverviewQuery({
    variables: {
      locationId: selectedLocationId,
      startDate: dateToApiFormat(dates[0]),
      endDate: dateToApiFormat(dates[dates.length - 1])
    }
  });

  const rosters = data?.rostersInDateRange;
  if (!rosters) {
    throw Error("Couldn't get rosters");
  }

  const details: RosterPeriodDetails = useMemo(() => {
    const publishedRosters = rosters.filter(r => r.status === 'Published');
    const publishedRosterCount = publishedRosters.length;
    const dateCount = dates.length;

    const details: RosterPeriodDetails = {
      titleString: option === 'month' ? "Next Month's Rosters" : "Next Week's Rosters",
      displayString: '',
      dates
    };

    if (dateCount === publishedRosterCount) {
      details.displayCheck = true;
      details.displayString =
        option === 'month' ? 'All rosters for next month are published.' : 'All rosters for next week are published.';
    } else if (publishedRosterCount === 0) {
      details.displayString =
        option === 'month'
          ? "Next month's rosters are yet to be published."
          : "Next week's rosters are yet to be published.";
    } else {
      const missingRosters = dateCount - publishedRosterCount;
      details.displayString =
        option === 'month'
          ? `Next month has ${missingRosters} missing roster${missingRosters === 1 ? '' : 's'}.`
          : `Next week has ${missingRosters} missing roster${missingRosters === 1 ? '' : 's'}.`;
    }

    if (!details.displayCheck) {
      const millisecondsAway = dates[0].getTime() - new Date().getTime();
      const daysAway = millisecondsToHours(millisecondsAway) / 24;

      if (daysAway <= warningDays) {
        details.warning = true;
      }
    }

    return details;
  }, [rosters, dates, option, warningDays]);

  return details;
};
